import {
  throwError as observableThrowError,
  forkJoin as observableForkJoin,
  of as observableOf,
  combineLatest as observableCombineLatest,
  Observable,
  Subscription,
  ConnectableObservable,
  ReplaySubject,
} from 'rxjs';

import {
  mergeMap,
  takeWhile,
  shareReplay,
  distinctUntilChanged,
  map,
  publishReplay,
  first,
  catchError,
  share,
  tap,
  switchMap,
  take,
} from 'rxjs/operators';
/**
 * Created by mimadmin on 4/19/2017.
 */
import {Injectable, OnDestroy} from '@angular/core';
import {NavigationBarConfiguration} from './uiconfiguration/navigationbarconfiguration';
import {SearchScopeConfiguration} from './uiconfiguration/searchscopeconfiguration';
import {BindingDescription} from './schema/bindingdescription';
import {AttributeTypeDescription} from './schema/attributetypedescription';
import {ObjectTypeDescription} from './schema/objecttypedescription';
import {DisplayAttribute} from '../resource/displayattribute';
import {User} from './currentuser/user';
import {ActionBarConfiguration} from './uiconfiguration/actionbarconfiguration';
import {DialogConfiguration} from './uiconfiguration/dialogconfiguration';
import {UIConfiguration} from './uiconfiguration/uiconfiguration';
import {SupportedLocaleConfiguration} from './uiconfiguration/supportedlocaleconfiguration';
import {HomePageConfiguration} from './uiconfiguration/homepageconfiguration';
import {ListRoutingConfiguration} from './schema/listroutingconfiguration';
import {Router} from '@angular/router';
import {EnvironmentConfigService} from '../core/services/environment-config.service';
import {DEFAULT_TIME_ZONE, TimeZoneConfiguration} from './uiconfiguration/timezoneconfiguration';
import {LayoutService} from '../layout/layout.service';
import {Endpoint} from '../layout/endpoint';
import {TimeZoneHandling} from './time-zone-handling';
import {StartupApiService} from '../core/services/startup-api.service';
import {SupportedLocaleConfigurationService} from './uiconfiguration/supportedlocaleconfiguration.service';

@Injectable()
export class CacheService implements OnDestroy {
  private alive: boolean = true;

  ngOnDestroy(): void {
    this.alive = false;
    this._subscriptions.forEach((subscrition: Subscription) => {
      subscrition.unsubscribe();
    });
  }

  // Current User Cache
  private currentUser: Observable<User>;

  // Schema Object Cache
  bindingDescriptions: Observable<BindingDescription[]> = null;
  attributeTypeDescriptions: Observable<AttributeTypeDescription[]> = null;
  resourceTypeDescriptions: Observable<ObjectTypeDescription[]> = null;

  // Portal Object Cache
  navigationBarConfigurations: Observable<NavigationBarConfiguration[]> = null;
  navigationBarConfigurationsMain: Observable<NavigationBarConfiguration[]> = null;
  navigationBarConfigurationsFooter: Observable<NavigationBarConfiguration[]> = null;

  searchScopeConfigurations: Observable<SearchScopeConfiguration[]> = null;
  actionBarConfigurations: Observable<ActionBarConfiguration[]> = null;
  dialogConfigurations: Observable<DialogConfiguration[]> = null;
  uiConfiguration: Observable<UIConfiguration> = null;
  supportedLocaleConfiguration: Observable<SupportedLocaleConfiguration> = null;
  homePageConfigurations: Observable<HomePageConfiguration[]> = null;
  listRoutingConfigurations: Observable<ListRoutingConfiguration[]> = null;
  timeZoneConfigurations: Observable<TimeZoneConfiguration[]> = null;

  timeZone: Observable<string> = null;
  currentTimeZone: string = DEFAULT_TIME_ZONE;

  recentGuids: string[] = [];
  private _subscriptions: Subscription[] = [];

  constructor(
    private startupApiService: StartupApiService,
    private supportedLocaleConfigurationService: SupportedLocaleConfigurationService,
    private layoutService: LayoutService,
    private envConfig: EnvironmentConfigService,
  ) {}

  public refreshAllData(): Observable<boolean> {
    // IMPORTANT: Load and connect locale configuration first as other services depend on it
    this.supportedLocaleConfigurationService.loadSupportedLocaleConfigurations();

    // Load all configurations at startup
    const configurations = this.startupApiService.loadAllConfigurations();

    // Transform homepage configuration
    const conHomepage = configurations.homepage.pipe(
      map(home => this.convertHomePageConfigurationsMain(home)),
      first(),
      publishReplay(1),
    ) as ConnectableObservable<any>;

    // Connect all observables immediately to start loading
    this.startupApiService.connectAll(configurations, this._subscriptions);
    this._subscriptions.push(conHomepage.connect());

    // Create list routing observable
    const conListRouting = observableCombineLatest(
      configurations.navigationBar.pipe(first()),
      conHomepage.pipe(first()),
      configurations.actionBar.pipe(first()),
      (
        navBars: NavigationBarConfiguration[],
        homePageConfigs: HomePageConfiguration[],
        actionBars: ActionBarConfiguration[],
      ) => {
        let listRoutingConfs: ListRoutingConfiguration[] = [];
        navBars.forEach(navBar => {
          if (navBar.sitetype === 'list' || (navBar.sitetype === 'timeline' && navBar.urlid)) {
            listRoutingConfs.push(
              new ListRoutingConfiguration(
                navBar.id,
                navBar.sitetype,
                navBar.linkedobject,
                navBar.urlid,
                navBar.actionbarresources,
                navBar.displayname,
              ),
            );
          }
        });
        homePageConfigs.forEach(hpc => {
          if (hpc.sitetype === 'list' || (hpc.sitetype === 'timeline' && hpc.urlid)) {
            listRoutingConfs.push(
              new ListRoutingConfiguration(
                hpc.id,
                hpc.sitetype,
                hpc.linkedobject,
                hpc.urlid,
                hpc.actionbarresources,
                hpc.displayname,
              ),
            );
          }
        });
        actionBars.forEach(ab => {
          if (ab.sitetype === 'list' || (ab.sitetype === 'timeline' && ab.urlid)) {
            listRoutingConfs.push(
              new ListRoutingConfiguration(
                ab.id,
                ab.sitetype,
                ab.linkedobject,
                ab.urlid,
                ab.actionbarresources,
                ab.displayname,
              ),
            );
          }
        });
        return listRoutingConfs;
      },
    ).pipe(first(), distinctUntilChanged(), publishReplay(1)) as ConnectableObservable<any>;
    this._subscriptions.push(conListRouting.connect());

    // Set up all observable references
    this.currentUser = configurations.currentUser;
    this.uiConfiguration = configurations.uiConfiguration;
    this.navigationBarConfigurations = configurations.navigationBar;
    this.navigationBarConfigurationsMain = this.navigationBarConfigurations.pipe(
      map(nav => this.convertNavigationBarConfigurationsMain(nav)),
      shareReplay(1),
    );
    this.navigationBarConfigurationsFooter = this.navigationBarConfigurations.pipe(
      map((navBars: NavigationBarConfiguration[]) =>
        this.convertNavigationBarConfigurationsFooter(navBars),
      ),
      shareReplay(1),
    ) as ConnectableObservable<any>;

    this.searchScopeConfigurations = configurations.searchScope;
    this.actionBarConfigurations = configurations.actionBar;
    this.dialogConfigurations = configurations.dialog;
    this.attributeTypeDescriptions = configurations.attributeTypes;
    this.resourceTypeDescriptions = configurations.objectTypes;
    this.bindingDescriptions = configurations.bindings;
    this.supportedLocaleConfiguration = configurations.locale;
    this.homePageConfigurations = conHomepage;
    this.listRoutingConfigurations = conListRouting;
    this.timeZoneConfigurations = configurations.timezone;

    const conTimeZoneValue = TimeZoneHandling.createTimeZoneObservable(this).pipe(
      publishReplay(1),
    ) as ConnectableObservable<any>;
    this._subscriptions.push(conTimeZoneValue.connect());
    this.timeZone = conTimeZoneValue;

    // Wait for all critical configurations to load at least once
    return observableForkJoin({
      currentUser: configurations.currentUser.pipe(first()),
      uiConfiguration: configurations.uiConfiguration.pipe(first()),
      navigationBar: configurations.navigationBar.pipe(first()),
      searchScope: configurations.searchScope.pipe(first()),
      actionBar: configurations.actionBar.pipe(first()),
      dialog: configurations.dialog.pipe(first()),
      attributeTypes: configurations.attributeTypes.pipe(first()),
      objectTypes: configurations.objectTypes.pipe(first()),
      bindings: configurations.bindings.pipe(first()),
      locale: configurations.locale.pipe(first()),
      homepage: conHomepage.pipe(first()),
      listRouting: conListRouting.pipe(first()),
      timezone: configurations.timezone.pipe(first()),
      timezoneValue: conTimeZoneValue.pipe(first()),
    }).pipe(
      tap(() => {
        // Subscribe to timezone changes after everything is loaded
        this.timeZone.pipe(takeWhile(() => this.alive)).subscribe((timezone: string) => {
          this.currentTimeZone = timezone;
        });
      }),
      map(() => true),
      catchError(error => {
        console.error('Failed to load configuration data:', error);
        return observableThrowError(error);
      }),
    );
  }

  public clearCache(): void {
    this.currentUser = null;
    this.bindingDescriptions = null;
    this.attributeTypeDescriptions = null;
    this.resourceTypeDescriptions = null;
    this.navigationBarConfigurations = null;
    this.navigationBarConfigurationsMain = null;
    this.navigationBarConfigurationsFooter = null;
    this.searchScopeConfigurations = null;
    this.actionBarConfigurations = null;
    this.dialogConfigurations = null;
    this.uiConfiguration = null;
    this.supportedLocaleConfiguration = null;
    this.homePageConfigurations = null;
    this.listRoutingConfigurations = null;
    this.timeZoneConfigurations = null;
    this.timeZone = null;
    this.recentGuids = [];

    this._subscriptions.forEach((subscription: Subscription) => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
      }
    });
    this._subscriptions = [];
  }

  public getCurrentUser(): Observable<User> {
    return this.currentUser;
  }

  public getDisplayAttributes(
    attributes: string[],
    objectType: string,
  ): Observable<DisplayAttribute[]> {
    return this.layoutService.endpoint$.pipe(
      take(1),
      switchMap((env: Endpoint) => {
        if (env.isCloud()) {
          objectType = objectType.toLowerCase();
        }

        let narray: DisplayAttribute[] = [];
        let displaynames: Observable<DisplayAttribute[]> = observableOf(narray);

        attributes.forEach(attr => {
          if (attr) {
            attr = attr.trim();
            if (env.isCloud()) {
              attr = attr.toLowerCase();
            }
            displaynames = displaynames.pipe(
              mergeMap((atts: DisplayAttribute[]) =>
                this.getDisplayAttribute(attr, objectType).pipe(
                  map((disatt: DisplayAttribute) => {
                    atts.push(disatt);
                    return atts;
                  }),
                ),
              ),
            );
          }
        });

        let sscsObs: Observable<DisplayAttribute>[] = [];
        sscsObs.push(
          this.getDisplayAttribute(
            this.envConfig.getAttributeName('DisplayName'),
            this.envConfig.getObjectTypeName('Resource'),
          ).pipe(first()),
        );
        sscsObs.push(
          this.getDisplayAttribute(
            this.envConfig.getAttributeName('Description'),
            this.envConfig.getObjectTypeName('Resource'),
          ).pipe(first()),
        );
        sscsObs.push(
          this.getDisplayAttribute(
            this.envConfig.getAttributeName('ObjectType'),
            this.envConfig.getObjectTypeName('Resource'),
          ).pipe(first()),
        );

        displaynames = displaynames.pipe(
          mergeMap((atts: DisplayAttribute[]) =>
            observableForkJoin(sscsObs).pipe(
              first(),
              map((attsNew: DisplayAttribute[]) => {
                if (atts == null || !Array.isArray(atts) || atts.length == 0) {
                  return attsNew;
                } else {
                  return atts;
                }
              }),
            ),
          ),
        );

        displaynames = displaynames.pipe(
          map((atts: DisplayAttribute[]) => {
            let displayAtts: DisplayAttribute[] = atts;
            if (atts == null || atts.length == 0) {
              displayAtts.push(
                DisplayAttribute.getDefaultAttribute(
                  this.envConfig.getAttributeName('DisplayName'),
                ),
              );
              displayAtts.push(
                DisplayAttribute.getDefaultAttribute(
                  this.envConfig.getAttributeName('Description'),
                ),
              );
              displayAtts.push(
                DisplayAttribute.getDefaultAttribute(this.envConfig.getAttributeName('ObjectType')),
              );
            }
            return displayAtts;
          }),
        );
        return displaynames;
      }),
    );
  }

  public getDisplayAttribute(systemName: string, objectType: string): Observable<DisplayAttribute> {
    return this.attributeTypeDescriptions.pipe(
      map((attributes: AttributeTypeDescription[]) => {
        let displayAtt: DisplayAttribute = null;
        attributes.forEach(attribute => {
          if (attribute.systemname === systemName) {
            displayAtt = <DisplayAttribute>{
              Name: systemName,
              DisplayName: systemName,
              Caption: systemName,
              DataType: attribute.datatype,
              Multivalued: attribute.multivalued,
              AttributeID: attribute.id,
              StringRegex: attribute.regularexpression,
              FileName: attribute.filename,
            };
          }
        });
        return displayAtt;
      }),
      mergeMap(attribute =>
        this.resourceTypeDescriptions.pipe(
          map((resources: ObjectTypeDescription[]) => {
            let displayAtt: DisplayAttribute = attribute;
            if (displayAtt != null) {
              resources.forEach(object => {
                if (object.systemname === objectType) {
                  displayAtt.ObjectID = object.id;
                }
              });
            }
            return displayAtt;
          }),
        ),
      ),
      mergeMap(attribute =>
        this.bindingDescriptions.pipe(
          map((bindings: BindingDescription[]) => {
            let displayAtt: DisplayAttribute = attribute;
            if (displayAtt != null) {
              bindings.forEach(binding => {
                if (
                  binding.boundobjecttype === displayAtt.ObjectID &&
                  binding.boundattributetype === displayAtt.AttributeID
                ) {
                  if (binding.displayname) {
                    displayAtt.DisplayName = binding.displayname;
                    displayAtt.Caption = binding.displayname;
                  }
                  if (binding.description) {
                    displayAtt.Description = binding.description;
                  }
                  if (binding.regularexpression) {
                    displayAtt.StringRegex = binding.regularexpression;
                  }
                  if (binding.filename) {
                    displayAtt.FileName = binding.filename;
                  }
                }
              });
            }
            return displayAtt;
          }),
        ),
      ),
    );
  }

  public getNavigationBarConfigurationsToDisplayMain(): Observable<NavigationBarConfiguration[]> {
    return this.navigationBarConfigurationsMain;
  }

  public getHomePageConfigurations(): Observable<HomePageConfiguration[]> {
    return this.homePageConfigurations;
  }

  public getHomePageConfigurationsById(urlid: string): Observable<HomePageConfiguration[]> {
    return this.homePageConfigurations.pipe(
      map((homePages: HomePageConfiguration[]) => {
        let objects: HomePageConfiguration[] = [];
        homePages.forEach((homePage: HomePageConfiguration) => {
          if (
            homePage.usagekeyword !== undefined &&
            homePage.usagekeyword !== null &&
            Array.isArray(homePage.usagekeyword) &&
            homePage.usagekeyword.indexOf(urlid) !== -1
          ) {
            objects.push(homePage);
            return;
          }
        });
        return objects;
      }),
    );
  }

  public getTimeZoneConfigurations(): Observable<TimeZoneConfiguration[]> {
    return this.timeZoneConfigurations;
  }

  public getTimeZone(): Observable<string> {
    return this.timeZone;
  }

  public getCurrentTimeZone(): string {
    return this.currentTimeZone;
  }

  private convertNavigationBarConfigurationsMain(
    navbars: NavigationBarConfiguration[],
  ): NavigationBarConfiguration[] {
    let finalNavbars: NavigationBarConfiguration[] = [];
    navbars.forEach(navbar => {
      if (navbar.displayinfooter != true) {
        finalNavbars.push(navbar);
      }
    });
    let parentNavbars: NavigationBarConfiguration[] = [];
    let parentItems: Number[] = [];
    finalNavbars.forEach(obj => {
      if (parentItems.indexOf(obj.parentorder) == -1) {
        let finalParentItem: NavigationBarConfiguration = obj;
        finalNavbars.forEach(obj2 => {
          if (obj2.parentorder == obj.parentorder && obj2.order < finalParentItem.order) {
            finalParentItem = obj2;
          }
        });
        parentItems.push(finalParentItem.parentorder);
        parentNavbars.push(finalParentItem);
      }
    });

    parentNavbars.forEach(obj => {
      let innerNavbars: NavigationBarConfiguration[] = [];
      finalNavbars.forEach((innerObj: NavigationBarConfiguration) => {
        if (innerObj.parentorder == obj.parentorder && innerObj !== obj) {
          if (innerNavbars == null) {
            innerNavbars = [];
            innerNavbars.push(<NavigationBarConfiguration>innerObj);
          } else {
            innerNavbars.push(<NavigationBarConfiguration>innerObj);
          }
        }
      });
      if (innerNavbars.length > 0) {
        obj.items = innerNavbars.sort(CacheService.compare);
      }
    });
    return parentNavbars.sort(CacheService.compareParents);
  }

  private convertHomePageConfigurationsMain(
    homeres: HomePageConfiguration[],
  ): HomePageConfiguration[] {
    let parentHomeres: HomePageConfiguration[] = [];
    let parentItems: Number[] = [];
    homeres.forEach(obj => {
      if (parentItems.indexOf(obj.parentorder) == -1) {
        let finalParentItem: HomePageConfiguration = obj;
        homeres.forEach(obj2 => {
          if (obj2.parentorder == obj.parentorder && obj2.order < finalParentItem.order) {
            finalParentItem = obj2;
          }
        });
        parentItems.push(finalParentItem.parentorder);
        parentHomeres.push(finalParentItem);
      }
    });

    parentHomeres.forEach(obj => {
      homeres.forEach((innerObj: HomePageConfiguration) => {
        if (innerObj.parentorder == obj.parentorder && innerObj !== obj) {
          let innerHomeres: HomePageConfiguration[] = obj.items;
          if (innerHomeres == null) {
            innerHomeres = [];
            innerHomeres.push(<HomePageConfiguration>innerObj);
          } else {
            innerHomeres.push(<HomePageConfiguration>innerObj);
          }
          obj.items = innerHomeres.sort(CacheService.compare);
        }
      });
    });
    return parentHomeres.sort(CacheService.compareParents);
  }

  private convertNavigationBarConfigurationsFooter(
    navbars: NavigationBarConfiguration[],
  ): NavigationBarConfiguration[] {
    let finalNavbars: NavigationBarConfiguration[] = [];
    let parentItems: Number[] = [];
    navbars.forEach(navbar => {
      if (navbar.displayinfooter == true) {
        finalNavbars.push(navbar);
      }
    });
    let parentNavbars: NavigationBarConfiguration[] = [];
    finalNavbars.forEach(obj => {
      if (parentItems.indexOf(obj.parentorder) == -1) {
        let finalParentItem: NavigationBarConfiguration = obj;
        finalNavbars.forEach(obj2 => {
          if (obj2.parentorder == obj.parentorder && obj2.order < finalParentItem.order) {
            finalParentItem = obj2;
          }
        });
        parentItems.push(finalParentItem.parentorder);
        parentNavbars.push(finalParentItem);
      }
    });

    parentNavbars.forEach(obj => {
      let innerNavbars: NavigationBarConfiguration[] = [];
      finalNavbars.forEach((innerObj: NavigationBarConfiguration) => {
        if (innerObj.parentorder == obj.parentorder && innerObj !== obj) {
          if (innerNavbars == null) {
            innerNavbars = [];
            innerNavbars.push(<NavigationBarConfiguration>innerObj);
          } else {
            innerNavbars.push(<NavigationBarConfiguration>innerObj);
          }
        }
      });
      if (innerNavbars.length > 0) {
        obj.items = innerNavbars.sort(CacheService.compare);
      }
    });
    return parentNavbars.sort(CacheService.compareParents);
  }

  public getNavigationBarConfigurationsToDisplayFooter(): Observable<NavigationBarConfiguration[]> {
    return this.navigationBarConfigurationsFooter;
  }

  public getResources(): Observable<ObjectTypeDescription[]> {
    return this.resourceTypeDescriptions;
  }

  public getAttributeTypeDescriptions(): Observable<AttributeTypeDescription[]> {
    return this.attributeTypeDescriptions;
  }

  private static compare(a, b) {
    if (a.order < b.order) return -1;
    if (a.order > b.order) return 1;
    return 0;
  }

  private static compareParents(a, b) {
    if (a.parentorder < b.parentorder) return -1;
    if (a.parentorder > b.parentorder) return 1;
    return 0;
  }

  public getSearchScopeConfigurationById(id: string): Observable<SearchScopeConfiguration> {
    return this.searchScopeConfigurations.pipe(
      map((scopes: SearchScopeConfiguration[]) => {
        let object: SearchScopeConfiguration = null;
        scopes.forEach((scope: SearchScopeConfiguration) => {
          if (scope.id === id) {
            object = scope;
            return;
          }
        });
        return object;
      }),
    );
  }

  public getSearchScopeConfigurationForObjectType(
    objectType: string,
  ): Observable<SearchScopeConfiguration[]> {
    return this.searchScopeConfigurations.pipe(
      map((scopes: SearchScopeConfiguration[]) => {
        let returnScopes: SearchScopeConfiguration[] = [];
        scopes.forEach((scope: SearchScopeConfiguration) => {
          if (scope.usagekeyword != null && scope.usagekeyword.indexOf(objectType) > -1) {
            returnScopes.push(scope);
          }
        });
        return returnScopes;
      }),
    );
  }

  public getFrequentAttributesForObjectType(objectType: string): Observable<string[]> {
    return this.searchScopeConfigurations.pipe(
      map((scopes: SearchScopeConfiguration[]) => {
        // Filter scopes that are relevant for this object type
        const relevantScopes = scopes.filter(scope => {
          // Check if scope is for this specific object type or applies to all resources
          if (scope.resultobjecttype === objectType || scope.resultobjecttype === 'Resource') {
            return true;
          }
          // Check if scope's usage keywords include this object type
          if (scope.usagekeyword && scope.usagekeyword.indexOf(objectType) > -1) {
            return true;
          }
          // Check for special "all eligible" case
          if (objectType === 'Resource' && scope.xpath && scope.xpath.includes('/*[')) {
            return true;
          }
          return false;
        });

        // Count attribute frequency from XPath expressions
        const attributeFrequency = new Map<string, number>();
        relevantScopes.forEach(scope => {
          if (scope.xpath) {
            const attributes = this.extractAttributesFromXPath(scope.xpath);
            attributes.forEach(attr => {
              attributeFrequency.set(attr, (attributeFrequency.get(attr) || 0) + 1);
            });
          }
        });

        // Sort by frequency and return top attributes
        return Array.from(attributeFrequency.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 7)
          .map(([attr]) => attr);
      }),
    );
  }

  private extractAttributesFromXPath(xpath: string): string[] {
    const attributes = new Set<string>();

    // Match patterns like: AttributeName=, AttributeName<, AttributeName>, AttributeName!=
    // Also match functions like starts-with(AttributeName, ...), contains(AttributeName, ...)
    const patterns = [
      /(\w+)\s*[=<>!]+/g, // Direct comparisons
      /starts-with\s*\(\s*(\w+)\s*,/g, // starts-with function
      /ends-with\s*\(\s*(\w+)\s*,/g, // ends-with function
      /contains\s*\(\s*(\w+)\s*,/g, // contains function
      /not\s*\(\s*(\w+)\s*[=<>]/g, // negated conditions
    ];

    patterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(xpath)) !== null) {
        const attrName = match[1];
        // Exclude ObjectType and function names
        if (
          attrName &&
          attrName !== 'ObjectType' &&
          attrName !== 'not' &&
          attrName !== 'and' &&
          attrName !== 'or' &&
          !attrName.startsWith('fn:') &&
          !attrName.startsWith('op:') &&
          !attrName.startsWith('xs:')
        ) {
          attributes.add(attrName);
        }
      }
    });

    return Array.from(attributes);
  }

  public getActionBarConfigurationById(id: string): Observable<ActionBarConfiguration> {
    return this.actionBarConfigurations.pipe(
      map((actionbars: ActionBarConfiguration[]) => {
        let object: ActionBarConfiguration = null;
        actionbars.forEach((actionbar: ActionBarConfiguration) => {
          if (actionbar.id === id) {
            object = actionbar;
            return;
          }
        });
        return object;
      }),
    );
  }

  public getActionBarConfigurationByDisplayName(
    displayName: string,
  ): Observable<ActionBarConfiguration> {
    return this.actionBarConfigurations.pipe(
      map((actionbars: ActionBarConfiguration[]) => {
        let object: ActionBarConfiguration = null;
        actionbars.forEach((actionbar: ActionBarConfiguration) => {
          if (actionbar.displayname === displayName) {
            object = actionbar;
            return;
          }
        });
        return object;
      }),
    );
  }

  public getActionBarConfigurationsForObjectType(
    objectType: string,
  ): Observable<ActionBarConfiguration[]> {
    return this.actionBarConfigurations.pipe(
      map((actionbars: ActionBarConfiguration[]) => {
        let returnBars: ActionBarConfiguration[] = [];
        actionbars.forEach((actionbar: ActionBarConfiguration) => {
          if (actionbar.usagekeyword != null && actionbar.usagekeyword.indexOf(objectType) > -1) {
            returnBars.push(actionbar);
          }
        });
        return returnBars;
      }),
    );
  }

  public getDialogConfigurationByObjectType(objecttype: string): Observable<DialogConfiguration> {
    return this.dialogConfigurations.pipe(
      switchMap((dgConfs: DialogConfiguration[]) => {
        return this.layoutService.endpoint$.pipe(
          take(1),
          map((env: Endpoint) => {
            let object: DialogConfiguration = null;
            dgConfs.forEach((dgConf: DialogConfiguration) => {
              let confObjType: string = env.isCloud() ? dgConf.urlid : dgConf.objecttype;
              if (confObjType === objecttype && dgConf.edit === true) {
                object = dgConf;
                return;
              }
            });
            return object;
          }),
        );
      }),
    );
  }

  public getDialogConfigurationByURLID(
    urlid: string,
    create: boolean = false,
  ): Observable<DialogConfiguration> {
    return this.dialogConfigurations.pipe(
      map((dgConfs: DialogConfiguration[]) => {
        let object: DialogConfiguration = null;
        dgConfs.forEach((dgConf: DialogConfiguration) => {
          if (dgConf.urlid === urlid && (dgConf.create || !create)) {
            object = dgConf;
            return;
          }
        });
        return object;
      }),
    );
  }

  public getDialogConfigurationByID(id: string): Observable<DialogConfiguration> {
    return this.dialogConfigurations.pipe(
      map((dgConfs: DialogConfiguration[]) => {
        let object: DialogConfiguration = null;
        dgConfs.forEach((dgConf: DialogConfiguration) => {
          if (dgConf.id === id) {
            object = dgConf;
            return;
          }
        });
        return object;
      }),
    );
  }

  public getNavigationBarConfigurationByAlias(
    alias: string,
  ): Observable<NavigationBarConfiguration> {
    return this.navigationBarConfigurations.pipe(
      map((navbar: NavigationBarConfiguration[]) =>
        this.toSingleNavigationBarConfiguration(navbar, alias),
      ),
    );
  }

  private toSingleNavigationBarConfiguration(
    navbars: any,
    alias: string,
  ): NavigationBarConfiguration {
    let object: NavigationBarConfiguration = null;
    navbars.forEach((nav: NavigationBarConfiguration) => {
      if (nav.urlid === alias) {
        object = nav;
        return;
      }
    });
    return object;
  }

  public getLanguageResource(): Observable<string> {
    //only use cache
    return this.uiConfiguration.pipe(
      map((uiConfiguration: UIConfiguration) => uiConfiguration.stringresources),
    );
  }

  public getNavigationBarConfiguration(): Observable<NavigationBarConfiguration[]> {
    return this.navigationBarConfigurations;
  }

  public getUIConfiguration(): Observable<UIConfiguration> {
    return this.uiConfiguration;
  }

  public getSupportedLocaleConfiguration(): Observable<SupportedLocaleConfiguration> {
    return this.supportedLocaleConfiguration.pipe(
      map(val => {
        return val;
      }),
    );
  }

  public getDisplayAttributesForResource(objectType: string): Observable<DisplayAttribute[]> {
    return this.resourceTypeDescriptions.pipe(
      map((resources: ObjectTypeDescription[]) => {
        let resourceid: string = null;
        resources.forEach(object => {
          if (object.systemname === objectType) {
            resourceid = object.id;
          }
        });
        return resourceid;
      }),
      mergeMap((resourceid: string) =>
        this.bindingDescriptions.pipe(
          map((bindings: BindingDescription[]) => {
            let atts: DisplayAttribute[] = [];
            bindings.forEach(binding => {
              if (binding.boundobjecttype == resourceid) {
                let displayAtt = <DisplayAttribute>{
                  DisplayName: binding.displayname,
                  Caption: binding.displayname,
                  AttributeID: binding.boundattributetype,
                  Description: binding.description,
                  StringRegex: binding.regularexpression,
                  FileName: binding.filename,
                };
                atts.push(displayAtt);
              }
            });
            return atts;
          }),
        ),
      ),
      mergeMap((attids: DisplayAttribute[]) =>
        this.attributeTypeDescriptions.pipe(
          map((attributes: AttributeTypeDescription[]) => {
            let atts: DisplayAttribute[] = [];
            attids.forEach((attbinding: DisplayAttribute) => {
              attributes.forEach(attribute => {
                if (attribute.id == attbinding.AttributeID) {
                  attbinding.Name = attribute.systemname;
                  if (attbinding.DisplayName === null || attbinding.DisplayName === undefined) {
                    attbinding.DisplayName = attribute.systemname;
                  }
                  if (attbinding.Description === null || attbinding.Description === undefined) {
                    attbinding.Description = attribute.description;
                  }
                  if (attbinding.Caption === null || attbinding.Caption === undefined) {
                    attbinding.Caption = attribute.systemname;
                  }
                  if (attbinding.FileName === null || attbinding.FileName === undefined) {
                    attbinding.FileName = attribute.filename;
                  }
                  attbinding.DataType = attribute.datatype;
                  attbinding.Multivalued = attribute.multivalued;
                  atts.push(attbinding);
                }
              });
            });
            return atts;
          }),
        ),
      ),
    );
  }

  public getAttributesForResource(objectType: string): Observable<string[]> {
    return this.resourceTypeDescriptions.pipe(
      map((resources: ObjectTypeDescription[]) => {
        let resourceid: string = null;
        resources.forEach(object => {
          if (object.systemname === objectType) {
            resourceid = object.id;
          }
        });
        return resourceid;
      }),
      mergeMap((resourceid: string) =>
        this.bindingDescriptions.pipe(
          map((bindings: BindingDescription[]) => {
            let atts: string[] = [];
            bindings.forEach(binding => {
              if (binding.boundobjecttype == resourceid) {
                atts.push(binding.boundattributetype);
              }
            });
            return atts;
          }),
        ),
      ),
      mergeMap((attids: string[]) =>
        this.attributeTypeDescriptions.pipe(
          map((attributes: AttributeTypeDescription[]) => {
            let atts: string[] = [];
            attids.forEach(attid => {
              attributes.forEach(attribute => {
                if (attribute.id == attid) {
                  atts.push(attribute.systemname);
                }
              });
            });
            return atts;
          }),
        ),
      ),
    );
  }

  public addRecentGuid(guid: string) {
    if (guid != null) {
      let index = this.recentGuids.indexOf(guid);
      if (index !== -1) {
        this.recentGuids.splice(index, 1);
      }
      this.recentGuids.splice(0, 0, guid);
      this.recentGuids.slice(-25);
    }
  }

  public addRecentGuids(guids: string[]) {
    if (guids != null && Array.isArray(guids) && guids.length > 0) {
      guids.forEach(guid => {
        let index = this.recentGuids.indexOf(guid);
        if (index !== -1) {
          this.recentGuids.splice(index, 1);
        }
        this.recentGuids.splice(0, 0, guid);
      });
      this.recentGuids.slice(-25);
    }
  }

  public getRecentGuids(): string[] {
    return [...new Set(this.recentGuids)];
  }

  public getListRoutingConfigurations(): Observable<ListRoutingConfiguration[]> {
    return this.listRoutingConfigurations;
  }

  public getListRoutingConfigurationByUrlID(
    urlId: string,
    siteType: string = 'list',
  ): Observable<ListRoutingConfiguration> {
    return this.getListRoutingConfigurations().pipe(
      map((lrcs: ListRoutingConfiguration[]) => {
        for (let lrc of lrcs) {
          if (lrc.urlId === urlId && lrc.siteType === siteType) {
            return lrc;
          }
        }
        return null;
      }),
    );
  }
}
